import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ArrowRight, Check, X, Menu, X as XIcon, Video, Users, TrendingUp, Clock, BarChart3, Target, Zap, Shield } from 'lucide-react';
import { motion } from 'framer-motion';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

const CALENDLY_URL = "https://calendly.com/livejobs/beratung?back=1&month=2026-06";

export default function RecruitingPage() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    // Load Recruiting Pixel (separate from main VALORYX pixel)
    if (!window._recruitingPixelLoaded) {
      const script = document.createElement('script');
      script.innerHTML = `
        !function(f,b,e,v,n,t,s)
        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)}(window, document,'script',
        'https://connect.facebook.net/en_US/fbevents.js');
        fbq('init', '1558624095784812');
        fbq('track', 'PageView');
      `;
      document.head.appendChild(script);
      window._recruitingPixelLoaded = true;
    } else if (typeof window.fbq === 'function') {
      window.fbq('track', 'PageView');
    }
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const faqs = [
    { q: 'Was kostet ein Employer Branding Video?', a: 'Je nach Umfang und Konzept liegen die Kosten zwischen CHF 4.500 und 15.000 pro Video. Wir bieten auch Abo-Modelle ab 4 Videos pro Jahr an — ideal für Unternehmen, die kontinuierlich rekrutieren. Bei Kombination mit einer Social Recruiting Kampagne entfallen die Initialkosten.' },
    { q: 'Wie lange dauert es, bis wir Ergebnisse sehen?', a: 'Die ersten Bewerbungen kommen typischerweise innerhalb von 2-3 Wochen nach Kampagnenstart. Bei einem unserer Kunden (öffentliche Verwaltung) konnten 35 Stellen in nur 3 Monaten besetzt werden.' },
    { q: 'Funktioniert das auch in unserer Branche?', a: 'Wir arbeiten branchenübergreifend — von Spitälern über Industrieunternehmen bis zur öffentlichen Verwaltung. Die einzige Voraussetzung: Sie haben offene Stellen und wollen als attraktiver Arbeitgeber wahrgenommen werden.' },
    { q: 'Wir haben intern ein HR-Team — brauchen wir das trotzdem?', a: 'Gerade dann. Ihr HR-Team kennt die Kultur, wir bringen die Werkzeuge: professionelle Videos, datengetriebene Kampagnen und 16 Jahre Recruiting-Expertise. Wir entlasten Ihr Team — statt es zu ersetzen.' },
    { q: 'Was unterscheidet euch von einer normalen Werbeagentur?', a: 'Werbeagenturen machen hübsche Videos. Wir machen Videos, die rekrutieren. Unser Team vereint Recruiting-Erfahrung mit Film-Expertise — wir wissen, was Bewerber sehen wollen und was Unternehmen brauchen.' },
    { q: 'Produziert ihr auch außerhalb der Schweiz?', a: 'Ja. Primär sind wir in der Deutschschweiz aktiv, produzieren aber auch auf Französisch und Italienisch. International haben wir Kooperationspartner in Deutschland, Österreich, USA und Hongkong.' },
  ];

  return (
    <div className="bg-white text-slate-900" style={{ fontFamily: "'Manrope', sans-serif" }} data-testid="recruiting-page">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/90 backdrop-blur-xl border-b border-slate-200' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <span className="font-black text-xl tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>VALORYX</span>
          <div className="hidden md:flex items-center gap-8">
            <a href="#leistungen" className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">Leistungen</a>
            <a href="#ergebnisse" className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">Ergebnisse</a>
            <a href="#ablauf" className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">Ablauf</a>
            <a href="#faq" className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">FAQ</a>
          </div>
          <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#2E7CF6] to-[#1B5FD4] text-white text-sm font-bold rounded-md hover:opacity-90 transition-all">
            Beratung buchen <ArrowRight className="w-4 h-4" />
          </a>
          <button className="md:hidden text-slate-900" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <XIcon className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-b border-slate-200 px-6 pb-4 space-y-3">
            <a href="#leistungen" onClick={() => setMobileMenuOpen(false)} className="block text-sm font-medium text-slate-600">Leistungen</a>
            <a href="#ergebnisse" onClick={() => setMobileMenuOpen(false)} className="block text-sm font-medium text-slate-600">Ergebnisse</a>
            <a href="#ablauf" onClick={() => setMobileMenuOpen(false)} className="block text-sm font-medium text-slate-600">Ablauf</a>
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="block w-full text-center px-5 py-2.5 bg-gradient-to-r from-[#2E7CF6] to-[#1B5FD4] text-white text-sm font-bold rounded-md">Beratung buchen</a>
          </div>
        )}
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-28 px-6 md:px-12">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 md:gap-16 items-center">
          <motion.div initial="hidden" animate="visible" variants={fadeUp}>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#2E7CF6]/20 bg-[#2E7CF6]/5 mb-6">
              <div className="w-2 h-2 rounded-full bg-[#2E7CF6]" />
              <span className="text-xs font-bold text-[#1B5FD4] tracking-wide uppercase">Employer Branding & Social Recruiting</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-slate-900 leading-[1.05] mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Offene Stellen besetzen.
              <br />
              <span className="text-[#2E7CF6]">Mit Videos, die wirken.</span>
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed max-w-xl mb-8">
              Professionelle Employer Branding Videos und Social Recruiting Kampagnen — von Experten mit 16 Jahren Erfahrung, die Recruiting und Filmproduktion vereinen.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="/recruiting/check" data-testid="hero-cta" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-[#2E7CF6] to-[#1B5FD4] text-white font-bold rounded-md hover:opacity-90 transition-all hover:shadow-lg">
                Kostenlose Beratung buchen <ArrowRight className="w-5 h-5" />
              </a>
            </div>
            <div className="flex items-center gap-6 mt-8 text-sm text-slate-500">
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-[#2E7CF6]" /> 30 Min. unverbindlich</span>
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-[#2E7CF6]" /> Inkl. Arbeitgeber-Markencheck</span>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.2 }} className="hidden md:block">
            <div className="relative">
              <div className="absolute -inset-4 bg-[#2E7CF6]/5 rounded-3xl -z-10" />
              <img src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=600&h=500&fit=crop" alt="Team collaboration" className="rounded-2xl w-full object-cover shadow-sm border border-slate-200" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-slate-50 border-y border-slate-200" id="ergebnisse">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Echte Ergebnisse. Keine Versprechen.</h2>
          </motion.div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { value: '35', unit: 'Stellen', desc: 'in 3 Monaten besetzt', sub: 'Öffentliche Verwaltung ZH' },
              { value: '35%', unit: '', desc: 'mehr Bewerbungen', sub: 'Gesundheitswesen' },
              { value: '60%', unit: '', desc: 'weniger Absprünge', sub: 'im Bewerbungsprozess' },
              { value: '50%', unit: '', desc: 'schnellere Rekrutierung', sub: 'Internationaler Konzern' },
            ].map((stat, i) => (
              <motion.div key={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={{ ...fadeUp, visible: { ...fadeUp.visible, transition: { ...fadeUp.visible.transition, delay: i * 0.1 } } }}>
                <p className="text-3xl md:text-4xl font-black text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>{stat.value}{stat.unit && <span className="text-lg font-bold text-slate-500 ml-1">{stat.unit}</span>}</p>
                <p className="text-sm text-[#2E7CF6] font-semibold mt-1">{stat.desc}</p>
                <p className="text-xs text-slate-400 mt-0.5">{stat.sub}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Problem */}
      <section className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="max-w-2xl mb-16">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 leading-tight mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>Warum klassisches Recruiting nicht mehr funktioniert</h2>
            <p className="text-lg text-slate-600 leading-relaxed">Stelleninserate allein reichen nicht. Die besten Kandidaten bewerben sich nicht — sie müssen überzeugt werden.</p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Users, title: 'Zu wenig Bewerbungen', desc: 'Stellenportale liefern immer weniger qualifizierte Kandidaten. Die besten Talente sind passiv — sie scrollen durch Social Media, nicht durch Jobinserate.' },
              { icon: Clock, title: 'Zu lange Time-to-Hire', desc: 'Monate vergehen, Stellen bleiben offen, Teams werden überlastet. Jeder Tag ohne Besetzung kostet Produktivität und Moral.' },
              { icon: Target, title: 'Kein Arbeitgeber-Profil', desc: 'Bewerber googlen Ihr Unternehmen — und finden nichts. Kein Video, keine Einblicke, keine Story. Ihre Konkurrenz zeigt sich. Sie nicht.' },
            ].map((item, i) => (
              <motion.div key={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={{ ...fadeUp, visible: { ...fadeUp.visible, transition: { ...fadeUp.visible.transition, delay: i * 0.1 } } }}
                className="border border-slate-200 rounded-xl p-8 hover:border-[#2E7CF6]/20 transition-colors">
                <item.icon className="w-8 h-8 text-[#2E7CF6] mb-5" strokeWidth={1.5} />
                <h3 className="text-lg font-semibold text-slate-900 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{item.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="leistungen" className="py-24 md:py-32 px-6 md:px-12 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="max-w-2xl mb-16">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 leading-tight mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>Zwei Leistungen. Ein Ziel: Stellen besetzen.</h2>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-8">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="bg-white border border-slate-200 rounded-xl p-8 hover:border-[#2E7CF6]/20 transition-colors">
              <div className="w-12 h-12 rounded-lg bg-[#2E7CF6]/5 border border-[#2E7CF6]/15 flex items-center justify-center mb-6">
                <Video className="w-6 h-6 text-[#2E7CF6]" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>Employer Branding Videos</h3>
              <p className="text-slate-600 text-sm leading-relaxed mb-4">Professionelle Videos, die Ihre Unternehmenskultur erlebbar machen — für Website, Social Media und Stelleninserate. Real gedreht, animiert oder KI-gestützt.</p>
              <ul className="space-y-2">
                {['Imagevideos & Reels für Social Media', 'Authentische Team- und Kultur-Einblicke', 'Optimiert für maximale Recruiting-Wirkung', 'Auch als Abo-Modell (4-12x pro Jahr)'].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                    <Check className="w-4 h-4 text-[#2E7CF6] mt-0.5 shrink-0" />{item}
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={{ ...fadeUp, visible: { ...fadeUp.visible, transition: { ...fadeUp.visible.transition, delay: 0.15 } } }} className="bg-white border border-slate-200 rounded-xl p-8 hover:border-[#2E7CF6]/20 transition-colors">
              <div className="w-12 h-12 rounded-lg bg-[#2E7CF6]/5 border border-[#2E7CF6]/15 flex items-center justify-center mb-6">
                <BarChart3 className="w-6 h-6 text-[#2E7CF6]" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>Social Recruiting Kampagnen</h3>
              <p className="text-slate-600 text-sm leading-relaxed mb-4">Datengetriebene Werbekampagnen auf Social Media — inkl. Setup, Grafiken, Videos, Recruiting-Funnel und Vorselektierung der Bewerber.</p>
              <ul className="space-y-2">
                {['Komplett-Setup inkl. Funnel & Creatives', 'Vorselektierung und erste Kommunikation', 'Monatliches Reporting & Optimierung', 'Kombination mit Videos = keine Initialkosten'].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                    <Check className="w-4 h-4 text-[#2E7CF6] mt-0.5 shrink-0" />{item}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section id="ablauf" className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="max-w-2xl mb-16">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 leading-tight mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>So läuft die Zusammenarbeit</h2>
          </motion.div>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { num: '01', title: 'Beratung & Analyse', desc: 'Wir analysieren Ihren IST-Zustand, Ihre Arbeitgebermarke und den konkreten Bedarf. Kostenlos und unverbindlich.' },
              { num: '02', title: 'Strategie & Konzept', desc: 'Basierend auf Ihren Zielen erstellen wir ein massgeschneidertes Recruiting-Konzept — Videos, Kampagnen oder beides.' },
              { num: '03', title: 'Produktion & Launch', desc: 'Unser Team produziert die Videos, baut den Funnel und startet die Kampagne. Sie fokussieren sich auf Ihr Tagesgeschäft.' },
              { num: '04', title: 'Bewerber & Optimierung', desc: 'Die ersten Bewerbungen kommen rein. Wir optimieren laufend und liefern monatliche Reports.' },
            ].map((step, i) => (
              <motion.div key={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={{ ...fadeUp, visible: { ...fadeUp.visible, transition: { ...fadeUp.visible.transition, delay: i * 0.1 } } }}>
                <span className="text-6xl font-black text-slate-200 leading-none" style={{ fontFamily: "'Outfit', sans-serif" }}>{step.num}</span>
                <h3 className="text-lg font-semibold text-slate-900 mt-3 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{step.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{step.desc}</p>
              </motion.div>
            ))}
          </div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="mt-14 text-center">
            <a href="/recruiting/check" className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-[#2E7CF6] to-[#1B5FD4] text-white font-bold rounded-md hover:opacity-90 transition-all">
              Kostenlose Beratung buchen <ArrowRight className="w-5 h-5" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* Why us */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="max-w-2xl mb-16">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 leading-tight mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>Warum Unternehmen uns vertrauen</h2>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { icon: Shield, title: '16 Jahre Erfahrung', desc: 'Kein Startup-Experiment — sondern bewährte Methoden von einem Team, das Recruiting und Content-Produktion seit über einem Jahrzehnt verbindet.' },
              { icon: Users, title: 'Generationen-Team', desc: 'Von Gen Z bis Boomer — unser Team versteht jede Zielgruppe. Das macht den Unterschied bei der Ansprache verschiedener Kandidaten-Profile.' },
              { icon: Zap, title: 'Recruiting + Film in einem', desc: 'Wir sind keine Werbeagentur, die "auch mal Recruiting macht". Unser Team hat Diplome in Werbefilm UND Erfahrung als Recruiter.' },
              { icon: TrendingUp, title: 'Messbare Ergebnisse', desc: 'Keine Vanity-Metrics. Wir messen Bewerbungen, Time-to-Hire und Qualität — und optimieren darauf. Monatliches Reporting inklusive.' },
            ].map((item, i) => (
              <motion.div key={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={{ ...fadeUp, visible: { ...fadeUp.visible, transition: { ...fadeUp.visible.transition, delay: i * 0.1 } } }}
                className="flex gap-5 bg-white border border-slate-200 rounded-xl p-6 hover:border-[#2E7CF6]/20 transition-colors">
                <div className="w-12 h-12 rounded-lg bg-[#2E7CF6]/5 border border-[#2E7CF6]/15 flex items-center justify-center shrink-0">
                  <item.icon className="w-6 h-6 text-[#2E7CF6]" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>{item.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-3xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="mb-12">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 leading-tight mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>Häufig gestellte Fragen</h2>
          </motion.div>
          <div className="space-y-0 border-t border-slate-200">
            {faqs.map((faq, i) => (
              <div key={i} className="border-b border-slate-200">
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between py-5 text-left">
                  <span className="text-base font-semibold text-slate-900 pr-4" style={{ fontFamily: "'Outfit', sans-serif" }}>{faq.q}</span>
                  <ChevronDown className={`w-5 h-5 text-slate-400 shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && <p className="pb-5 text-sm text-slate-600 leading-relaxed -mt-1">{faq.a}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 md:py-24 px-6 md:px-12 bg-gradient-to-r from-[#2E7CF6] to-[#1B5FD4]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white leading-tight mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>Bereit, Ihre offenen Stellen zu besetzen?</h2>
          <p className="text-lg text-white/80 leading-relaxed mb-8 max-w-xl mx-auto">30 Minuten Beratung — inkl. Analyse Ihrer Arbeitgebermarke und konkretem Massnahmenplan. Kostenlos und unverbindlich.</p>
          <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-8 py-4 bg-white text-[#1B5FD4] font-bold rounded-md hover:bg-[#2E7CF6]/5 transition-all">
            Beratung buchen <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 py-12 px-6 md:px-12">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-8">
            <span className="text-white font-black text-lg tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>VALORYX</span>
            <span className="text-slate-500 text-sm">2026 Alle Rechte vorbehalten.</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-slate-400">
            <Link to="/impressum" className="hover:text-white transition-colors">Impressum</Link>
            <Link to="/datenschutz" className="hover:text-white transition-colors">Datenschutzerklärung</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
